import React, { useCallback, useMemo, useState } from "react";
import { AnyVelavuEvent, EventCategory, VelavuAPI } from "velavu-js-api";
import { DateRange, useDateRange } from "../../../../data/date-range";
import DatePicker from "../../../../elements/date-picker";
import Divider from "../../../../elements/divider";
import EventList from "../../../../elements/event-list";
import { useProvideContentRefreshable } from "../../../../elements/refresh-context";
import {
	compareEventAsc,
	compareEventDesc,
} from "../../../../helper/events-helper";
import { useCancellableEffect } from "../../../../helper/hook-helper";
import * as styles from "./geofence-detail-events.module.scss";

export interface GeofenceDetailEventsProps {
	geofenceID: string;
}

const GEOFENCE_EVENT_CATEGORIES: readonly EventCategory[] = [
	EventCategory.GeofenceEntered,
	EventCategory.GeofenceExited,
];

function loadGeofenceEvents(
	dateRange: DateRange,
	geofenceID: string,
): Promise<AnyVelavuEvent[]> {
	const promises = GEOFENCE_EVENT_CATEGORIES.map((eventCategory) => {
		return VelavuAPI.events.getAllEvents({
			category: eventCategory,
			since: dateRange.start,
			until: dateRange.end,
			geofenceID: geofenceID,
		});
	});

	return Promise.all(promises).then((results) => {
		return results.map((paginatedResult) => paginatedResult.data).flat();
	});
}

export default function GeofenceDetailEvents(props: GeofenceDetailEventsProps) {
	//Last week -> Today
	const [dateRange, setDateRange] = useDateRange(7);
	const [sortNew, setSortNew] = useState(true);

	//Load the events
	const [events, setEvents] = useState<AnyVelavuEvent[] | undefined>(
		undefined,
	);
	useCancellableEffect(
		(addPromise) => {
			addPromise(loadGeofenceEvents(dateRange, props.geofenceID))
				.then(setEvents)
				.catch(console.warn);
		},
		[setEvents, props.geofenceID, dateRange],
	);

	//Register the refresh callback
	const refreshEvents = useCallback(() => {
		return loadGeofenceEvents(dateRange, props.geofenceID).then(setEvents);
	}, [setEvents, props.geofenceID, dateRange]);
	useProvideContentRefreshable(refreshEvents);

	//Sort the events
	const sortedEvents = useMemo(() => {
		return events?.sort(sortNew ? compareEventDesc : compareEventAsc);
	}, [events, sortNew]);

	return (
		<>
			<DatePicker
				range={dateRange}
				onUpdate={setDateRange}
				sortNew={sortNew}
				setSortNew={setSortNew}
			/>

			<Divider />

			<EventList
				className={styles.list}
				events={sortedEvents}
				includeEventOrigin
			/>
		</>
	);
}
